import { type PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

import { ContentMarginLine, GridRule } from '@/components/v2/GridLines';
import { LayoutGrid, LayoutSubgrid } from '@/components/v2/Page/LayoutGrid';

import { useHeaderUIRefs } from './HeaderUIProvider';
import { Ticker } from './Ticker';

const backgroundTextureCss = css`
  background-color: ${({ theme }) => theme.paletteCssVars.fg100};

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background-image: ${({ theme }) => theme.paperTextureCssVars.img};
    background-repeat: repeat;
    background-size: ${({ theme }) => theme.paperTextureCssVars.size};
    mix-blend-mode: ${({ theme }) => theme.paperTextureCssVars.mixBlendMode};
    opacity: ${({ theme }) => theme.paperTextureCssVars.opacity};
    pointer-events: none;
  }
`;

const TopGridLines = styled(LayoutSubgrid)`
  position: relative;
  background-color: ${({ theme }) => theme.paletteCssVars.fg100};
  ${backgroundTextureCss}

  height: 1.25rem;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    height: 0.75rem;
  }
`;

function HeaderGridLines() {
  const { gridlineRef } = useHeaderUIRefs();
  return (
    <TopGridLines ref={gridlineRef} aria-hidden="true">
      <ContentMarginLine data-minor />
      <GridRule $column={4} />
      <GridRule $column={10} />
      <ContentMarginLine data-minor data-right />
    </TopGridLines>
  );
}

const TickerWrapper = styled(LayoutSubgrid)`
  position: relative;
  align-self: flex-start;
  ${backgroundTextureCss}
`;

export function Header({ children }: PropsWithChildren) {
  return (
    <LayoutGrid style={{ position: 'fixed', zIndex: 3 }}>
      <HeaderGridLines />
      <LayoutSubgrid as="header">
        <TickerWrapper data-pile>
          <Ticker />
        </TickerWrapper>
        <LayoutSubgrid data-pile>{children}</LayoutSubgrid>
      </LayoutSubgrid>
    </LayoutGrid>
  );
}
